import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCardIcon } from '@heroicons/react/24/outline';
import { useAdminSubscriptions, useExtendSubscription, useChangeSubscriptionPlan } from '@/hooks/useAdmin';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Pagination } from '@/components/ui/Pagination';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { formatDate } from '@/utils/format';

const statusVariant: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = { active: 'success', trial: 'info', expired: 'error', cancelled: 'default', expiring_soon: 'warning', grace_period: 'warning' };

export function AdminSubscriptionsPage() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [statusFilter, setStatusFilter] = useState('');
  const [expiringDays, setExpiringDays] = useState<number | undefined>();
  const [extendModal, setExtendModal] = useState<{ userId: string; name: string } | null>(null);
  const [extendDays, setExtendDays] = useState('30');
  const [planModal, setPlanModal] = useState<{ userId: string; name: string; currentPlan: string; currentMaxSites: number; currentPrice: number } | null>(null);
  const [planForm, setPlanForm] = useState({ plan: '', maxSites: 3, priceAmount: 0 });

  const { data, isLoading, isError, refetch } = useAdminSubscriptions({ page, perPage, status: statusFilter, expiringDays });
  const extendSub = useExtendSubscription();
  const changePlan = useChangeSubscriptionPlan();

  const handleExtend = async () => {
    if (!extendModal) return;
    await extendSub.mutateAsync({ userId: extendModal.userId, days: Number(extendDays) });
    setExtendModal(null);
    setExtendDays('30');
  };

  const openChangePlan = (item: { user: { id: string; firstName: string; lastName: string }; subscription: { plan: string; maxSites?: number; priceAmount?: number } }) => {
    const sub = item.subscription;
    setPlanForm({ plan: sub.plan, maxSites: sub.maxSites ?? 3, priceAmount: sub.priceAmount ?? 0 });
    setPlanModal({ userId: item.user.id, name: `${item.user.firstName} ${item.user.lastName}`, currentPlan: sub.plan, currentMaxSites: sub.maxSites ?? 3, currentPrice: sub.priceAmount ?? 0 });
  };

  const handleChangePlan = async () => {
    if (!planModal) return;
    await changePlan.mutateAsync({ userId: planModal.userId, data: planForm });
    setPlanModal(null);
  };

  if (isError) return <ErrorState onRetry={() => refetch()} />;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('admin.subscriptions')}</h1>

      <Card>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
          <Select options={[{ value: '', label: t('common.all') }, { value: 'active', label: t('common.active') }, { value: 'trial', label: t('subscription.statuses.trial') }, { value: 'expiring_soon', label: t('subscription.statuses.expiring_soon') }, { value: 'expired', label: t('subscription.statuses.expired') }, { value: 'cancelled', label: t('subscription.statuses.cancelled') }]} value={statusFilter} onChange={setStatusFilter} />
          <Select options={[{ value: '', label: t('admin.expiringWithin') }, { value: '7', label: '7 ' + t('admin.days') }, { value: '30', label: t('admin.days30') }, { value: '90', label: t('admin.days90') }]} value={expiringDays?.toString() ?? ''} onChange={(v) => setExpiringDays(v ? Number(v) : undefined)} />
        </div>

        {isLoading ? (
          <div className="space-y-3">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}</div>
        ) : data?.items?.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    <th className="px-4 py-2">{t('admin.user')}</th>
                    <th className="px-4 py-2">{t('subscription.plan')}</th>
                    <th className="px-4 py-2">{t('common.status')}</th>
                    <th className="px-4 py-2">{t('subscription.maxSites')}</th>
                    <th className="px-4 py-2">{t('common.expires')}</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item: { user: { id: string; firstName: string; lastName: string; email: string }; subscription: { id: string; plan: string; status: string; maxSites: number; expiresAt?: string; priceAmount?: number } }) => (
                    <tr key={item.subscription.id} className="border-b border-gray-50 dark:border-gray-800">
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900 dark:text-gray-100">{item.user.firstName} {item.user.lastName}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{item.user.email}</p>
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">{t(`subscription.plans.${item.subscription.plan}`, { defaultValue: item.subscription.plan })}</td>
                      <td className="px-4 py-3"><Badge label={item.subscription.status} variant={statusVariant[item.subscription.status] ?? 'default'} /></td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{item.subscription.maxSites}</td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{item.subscription.expiresAt ? formatDate(item.subscription.expiresAt) : '-'}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => setExtendModal({ userId: item.user.id, name: `${item.user.firstName} ${item.user.lastName}` })}>{t('admin.extendSubscription')}</Button>
                          <Button variant="ghost" size="sm" onClick={() => openChangePlan(item)}>{t('admin.changePlan')}</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.totalPages > 1 && <div className="mt-4 px-4"><Pagination page={data.page} perPage={data.perPage} total={data.total} totalPages={data.totalPages} onPageChange={setPage} onPerPageChange={(pp) => { setPerPage(pp); setPage(1); }} /></div>}
          </>
        ) : (
          <EmptyState icon={CreditCardIcon} title={t('admin.noSubscriptions')} />
        )}
      </Card>

      {/* Extend subscription modal */}
      <Modal isOpen={!!extendModal} onClose={() => setExtendModal(null)} title={t('admin.extendSubscription')} size="sm">
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">{extendModal?.name}</p>
          <div className="flex gap-2">
            {['30', '90', '365'].map((d) => (
              <Button key={d} variant={extendDays === d ? 'primary' : 'outline'} size="sm" onClick={() => setExtendDays(d)}>
                {d === '30' ? t('admin.days30') : d === '90' ? t('admin.days90') : t('admin.year1')}
              </Button>
            ))}
          </div>
          <Input label={t('admin.custom')} type="number" min="1" value={extendDays} onChange={(e) => setExtendDays(e.target.value)} />
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setExtendModal(null)}>{t('common.cancel')}</Button>
            <Button onClick={handleExtend} loading={extendSub.isPending}>{t('common.save')}</Button>
          </div>
        </div>
      </Modal>

      {/* Change plan modal */}
      <Modal isOpen={!!planModal} onClose={() => setPlanModal(null)} title={t('admin.changePlan')} size="sm">
        {planModal && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">{planModal.name}</p>
            <Select label={t('subscription.plan')} options={[{ value: 'free_trial', label: t('subscription.plans.free_trial') }, { value: 'monthly', label: t('subscription.plans.monthly') }, { value: 'yearly', label: t('subscription.plans.yearly') }, { value: 'lifetime', label: t('subscription.plans.lifetime') }, { value: 'custom', label: t('subscription.plans.custom') }]} value={planForm.plan} onChange={(v) => setPlanForm((f) => ({ ...f, plan: v }))} />
            <Input label={t('subscription.maxSites')} type="number" min="1" value={String(planForm.maxSites)} onChange={(e) => setPlanForm((f) => ({ ...f, maxSites: Number(e.target.value) || 1 }))} />
            <Input label={t('common.price')} type="number" min="0" step="0.01" value={String(planForm.priceAmount)} onChange={(e) => setPlanForm((f) => ({ ...f, priceAmount: Number(e.target.value) || 0 }))} />
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setPlanModal(null)}>{t('common.cancel')}</Button>
              <Button onClick={handleChangePlan} loading={changePlan.isPending}>{t('common.save')}</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
